import {
  type UpdateAssetParams,
  type UpdateAssetVersionParams,
  activateAssetVersionReturnSchema,
  assetSchema,
  baseAssetVersionSchema,
  deleteAssetVersionReturnSchema,
  getAssetsRootSchema,
  getAssetsSchema,
  getMultipleAssetsSchema,
  updateAssetReturnSchema,
  variantUrlsSchema,
} from '@/types/asset';
import { DAM_URL, INTERNAL_V1, damRequest } from '@/hooks/damRequest';
import { assetNoteSchema, getAssetNotesSchema } from '@/types/notes';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { axiosInstance as axios } from '@/lib/axios';
import { pageSizeOptions } from '@/types/pagination';
import { getAuditsSchema } from '@/types/audit';
import { log } from '@/utilities/log';

const BASE = `${INTERNAL_V1}/assets/`;

const getAsset = async (params: { id: string; silent?: boolean }, token?: string) => {
  const response = await damRequest('GET', `${BASE}${params.id}`, token, params.silent ? { silent: true } : undefined);
  return assetSchema.parse(response);
};

const getAssets = async (params?: { pagination?: { offset: number; limit: number } }, token?: string) => {
  const query = new URLSearchParams({
    offset: String(params?.pagination?.offset ?? 0),
    limit: String(params?.pagination?.limit ?? pageSizeOptions[0]),
  }).toString();

  const response = await damRequest('GET', `${INTERNAL_V1}/assets?${query}`, token);
  return getAssetsSchema.parse(response);
};

const getFolderAssets = async (
  params?: {
    folderId: string;
    pagination?: { offset: number; limit: number };
    includeSubFolderAssets?: boolean;
    includeChildren?: boolean;
    sort?: { order: string; value: string };
    queryString?: string;
    assetId?: string;
  },
  token?: string,
) => {
  const query = new URLSearchParams({
    offset: String(params?.pagination?.offset ?? 0),
    limit: String(params?.pagination?.limit ?? pageSizeOptions[0]),
    includeAssets: '1',
    includeChildren: params?.includeChildren ? '1' : '0',
    includeAllSubFolders: params?.includeSubFolderAssets ? '1' : '0',
    filterQuery: params?.queryString ?? '',
    sortBy: params?.sort?.value ?? SortOrderValues.CREATED_AT,
    sortOrder: params?.sort?.order ?? SortOrderOptions.ASC,
    ...(params?.assetId ? { assetId: params.assetId } : {}),
  }).toString();

  const response = await damRequest('GET', `${INTERNAL_V1}/folders/${params?.folderId}?${query}`, token, {});
  //Schema for response, because response has nodeId param
  return getAssetsRootSchema.parse(response);
};

const getAlbumAssets = async (
  params?: {
    albumId: string;
    pagination?: { offset: number; limit: number };
    includeChildren?: boolean;
    sort?: { order: string; value: string };
    queryString?: string;
    assetId?: string;
  },
  token?: string,
) => {
  const query = new URLSearchParams({
    offset: String(params?.pagination?.offset ?? 0),
    limit: String(params?.pagination?.limit ?? pageSizeOptions[0]),
    includeAssets: '1',
    includeChildren: params?.includeChildren ? '1' : '0',
    filterQuery: params?.queryString ?? '',
    sortBy: params?.sort?.value ?? SortOrderValues.CREATED_AT,
    sortOrder: params?.sort?.order ?? SortOrderOptions.ASC,
    ...(params?.assetId ? { assetId: params.assetId } : {}),
  }).toString();

  const response = await damRequest('GET', `${INTERNAL_V1}/albums/${params?.albumId}?${query}`, token, {});

  return getAssetsRootSchema.parse(response);
};

const createAsset = async (
  params: {
    folderId: string;
    albumIds?: Array<string>;
    file: File;
    sessionId: string;
    onProgress: (progress: number, id: string, sessionId: string) => void;
    assetId?: string;
    originalFileName?: string;
    temporaryId: string;
    onError: (id: string, error: any, sessionId: string) => void;
    metadata: object;
    workflows: object;
    tags: Array<string>;
    name: string;
    description: string;
    rating: number;
    /** Present only for a contributor upload; ties the created asset back to its submission link. */
    linkId?: string;
  },
  token?: string,
) => {
  try {
    const baseUrl = `${DAM_URL}${INTERNAL_V1}`;
    const headers = {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };

    log(`Processing file via claim ${params.file.name}`);

    log(`Creating upload URL for file ${params.file.name}`);
    const upload = (
      await axios.post(
        `${baseUrl}/uploads`,
        {
          parentFolderId: params.folderId,
          ...(params.linkId && { linkId: params.linkId }),
        },
        { headers },
      )
    ).data;
    log(`Upload URL created: ${upload.url}`);

    log(`Uploading file ${params.file.name}`);
    await axios.put(upload.url, params.file, {
      headers: { 'Content-Type': 'application/octet-stream' },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && params.onProgress) {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          params.onProgress?.(progress, params.temporaryId, params.sessionId);
        }
      },
    });

    log(`Claiming upload ${upload.id} for file ${params.file.name}`);
    const claimResponse = await axios.post(
      `${baseUrl}/uploads/${upload.id}/claim`,
      {
        /** The backend rejects assetId/albumIds outright once linkId is set; omit both here
         * rather than relying on them happening to stay empty for a contributor upload. */
        ...(params.assetId && !params.linkId && { assetId: params.assetId }),
        parentFolderId: params.folderId,
        ...(!params.linkId && { albumIds: params.albumIds }),
        originalFileName: params.originalFileName ?? params.file.name,
        metadata: params.metadata,
        workflows: params.workflows,
        tags: params.tags,
        name: params.name,
        rating: params.rating,
        description: params.description,
        ...(params.linkId && { linkId: params.linkId }),
      },
      { headers },
    );

    return claimResponse.data;
  } catch (error: any) {
    if (error?.response?.status === 429) {
      throw error;
    }

    /** A contributor's claim rides entirely on the link's grant, so once it 403s here it
     * will 403 for every subsequent file too — unlike an ordinary per-file failure. */
    if (error?.response?.status === 403 && params.linkId) {
      throw error;
    }

    if (params.onError && params.temporaryId) {
      params.onError(params.temporaryId, error.message ?? 'Failed to upload asset', params.sessionId);
    } else {
      throw new Error(`Failed to upload asset`, { cause: error });
    }
  }
};

const createAssetVersion = async (
  params: {
    folderId: string;
    file: File;
    onProgress?: (progress: number) => void;
    originalFileName?: string;
    assetId?: string;
    activateNewVersion: boolean;
  },
  token?: string,
) => {
  const baseUrl = `${DAM_URL}${INTERNAL_V1}`;
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };

  log(`Creating upload URL for file ${params.file.name}`);
  const upload = (await axios.post(`${baseUrl}/uploads`, {}, { headers })).data;
  log(`Upload URL created: ${upload.url}`);

  log(`Uploading new file's version: ${params.file.name}`);
  await axios.put(upload.url, params.file, {
    headers: { 'Content-Type': 'application/octet-stream' },
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total) {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        params.onProgress?.(progress);
      }
    },
  });

  log(`Claiming upload ${upload.id} for file ${params.file.name}`);
  const response = await axios.post(
    `${baseUrl}/assets/${params.assetId}/versions`,
    {
      uploadId: upload.id,
      originalFileName: params.originalFileName ?? params.file.name,
      setActive: params.activateNewVersion,
    },
    { headers },
  );

  return response.data;
};

// Adding context here for now to use it in the hook.
const updateAsset = async (
  { id, body }: { id?: string | null; body: UpdateAssetParams; context?: string },
  token?: string,
) => {
  const response = await damRequest('PATCH', `${BASE}${id}`, token, { body });

  return updateAssetReturnSchema.parse(response);
};

const getAssetAuditLog = async (
  params?: { id?: string; pagination?: { offset: number; limit: number } },
  token?: string,
) => {
  const query = new URLSearchParams({
    offset: String(params?.pagination?.offset ?? 0),
    limit: String(params?.pagination?.limit ?? pageSizeOptions[0]),
  }).toString();

  const response = await damRequest('GET', `${BASE}${params?.id}/audit?${query}`, token);
  return getAuditsSchema.parse(response);
};

const deleteAssetVersion = async (
  { asset_id, version_id }: { asset_id?: string | null; version_id?: string | null },
  token?: string,
) => {
  const response = await damRequest('DELETE', `${BASE}${asset_id}/versions/${version_id}`, token);

  return deleteAssetVersionReturnSchema.parse(response);
};

const activateAssetVersion = async (
  { asset_id, version_id }: { asset_id?: string | null; version_id?: string | null },
  token?: string,
) => {
  const response = await damRequest('POST', `${BASE}${asset_id}/versions/${version_id}/activate`, token);

  return activateAssetVersionReturnSchema.parse(response);
};

const createAssetNotes = async ({ assetId, body }: { assetId: string; body: object }, token?: string) => {
  const response = await damRequest('POST', `${BASE}${assetId}/notes`, token, { body });

  return assetNoteSchema.parse(response);
};

const getAssetNotes = async (id: string, token?: string) => {
  const response = await damRequest('GET', `${BASE}${id}/notes`, token);

  return getAssetNotesSchema.parse(response);
};

const updateAssetNotes = async (
  { assetId, noteId, body }: { assetId: string; noteId: string; body: object },
  token?: string,
) => {
  const response = await damRequest('PUT', `${BASE}${assetId}/notes/${noteId}`, token, { body });

  return assetNoteSchema.parse(response);
};

const deleteAssetNotes = async ({ assetId, noteId }: { assetId: string; noteId: string }, token?: string) => {
  const response = await damRequest('DELETE', `${BASE}${assetId}/notes/${noteId}`, token);

  return assetNoteSchema.parse(response);
};

const undeleteAssetNotes = async ({ assetId, noteId }: { assetId: string; noteId: string }, token?: string) => {
  const response = await damRequest('POST', `${BASE}${assetId}/notes/${noteId}/undelete`, token);

  return assetNoteSchema.parse(response);
};

const duplicateAsset = async (
  { id, destinationFolderId }: { id: string; destinationFolderId: string },
  token?: string,
) => {
  const response = await damRequest('POST', `${BASE}${id}/duplicate`, token, {
    body: { destinationFolderId },
  });

  return updateAssetReturnSchema.parse(response);
};

const getMultipleAssets = async (
  params: {
    folderId?: string;
    albumId?: string;
    assetIds?: Array<string>;
    searchQuery?: string;
    includeSubFolderAssets?: boolean;
  },
  token?: string,
) => {
  const query = new URLSearchParams({
    ...(params.folderId
      ? { folderId: params.folderId, includeSubFolderAssets: params.includeSubFolderAssets ? '1' : '0' }
      : {}),
    ...(params.albumId ? { albumId: params.albumId } : {}),
    ...(params.searchQuery ? { searchQuery: params.searchQuery } : {}),
  });

  if (params.assetIds && Array.isArray(params.assetIds)) {
    params.assetIds.forEach((id) => query.append('assetIds[]', id)); // Laravel expects assetIds[] as the array format
  }

  const response = await damRequest('GET', `${BASE}multiple?${query.toString()}`, token);
  return getMultipleAssetsSchema.parse(response);
};

export const updateAssetVersion = async (
  {
    assetId,
    versionId,
    body,
  }: {
    assetId: string;
    versionId: string;
    body: UpdateAssetVersionParams;
  },
  token?: string,
) => {
  const response = await damRequest('PATCH', `${BASE}${assetId}/versions/${versionId}`, token, {
    body,
  });

  return baseAssetVersionSchema.parse(response);
};

export const getVariantUrls = async (
  params: { assetIds: Array<string>; variantSlug?: string },
  token?: string,
): Promise<Record<string, string>> => {
  const response = await damRequest('POST', `${INTERNAL_V1}/assets/variant-urls`, token, {
    body: { assetIds: params.assetIds, variantSlug: params.variantSlug ?? 'thumbnail-internal' },
  });
  return variantUrlsSchema.parse(response ?? {});
};

export {
  getAsset,
  getAssets,
  getFolderAssets,
  getAlbumAssets,
  getAssetAuditLog,
  createAsset,
  createAssetVersion,
  updateAsset,
  duplicateAsset,
  deleteAssetVersion,
  activateAssetVersion,
  createAssetNotes,
  getAssetNotes,
  updateAssetNotes,
  deleteAssetNotes,
  undeleteAssetNotes,
  getMultipleAssets,
};
